import React, { useEffect } from 'react';
import { ArrowRight, ChevronRight, MessageCircle, Sparkles } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { useLanguage } from '../context/LanguageContext';
import { getWsjCommercialWhatsappUrl } from '@/lib/wsjCommercialWhatsapp';
import { getDigitalInviteOrderWhatsappUrl } from '@/lib/digitalInvitesWhatsapp';
import { DigitalInvitesHeroMockups } from '../components/digital-invites/DigitalInvitesHeroMockups';
import { DigitalInvitesCatalogSection } from '../components/digital-invites/DigitalInvitesCatalogSection';
import { DigitalInvitesBenefitsSection } from '../components/digital-invites/DigitalInvitesBenefitsSection';
import { DigitalInvitesHowToOrderSection } from '../components/digital-invites/DigitalInvitesHowToOrderSection';

const DigitalInvites = () => {
  const { t, language } = useLanguage();
  const p = t.digitalInvitesPage;

  useEffect(() => {
    const prev = document.title;
    document.title = p.metaTitle;
    return () => {
      document.title = prev;
    };
  }, [p.metaTitle]);

  const openWhatsapp = () => {
    window.open(getWsjCommercialWhatsappUrl(p.whatsappMessage), '_blank', 'noopener,noreferrer');
  };

  const modelsMenuUrl = 'https://pedidos.wsjsolucoesdigitais.com/app/wsj-convites-digitais/menu';

  const openOrderWhatsapp = () => {
    window.open(getDigitalInviteOrderWhatsappUrl(p.orderWhatsappModelGeneric), '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="min-h-screen w-full min-w-0 max-w-full overflow-x-hidden">
      <Header />
      <main className="relative overflow-x-hidden pt-24 sm:pt-28 pb-8">
        <div className="absolute inset-0 tech-grid opacity-[0.18] pointer-events-none" />
        <div className="absolute top-24 left-1/4 w-[min(420px,calc(100vw-2rem))] max-w-[100vw] h-[min(420px,70vw)] bg-amber-500/[0.06] rounded-full blur-[100px] pointer-events-none" />
        <div className="absolute bottom-40 right-0 w-[min(380px,calc(100vw-2rem))] max-w-[100vw] h-[min(380px,65vw)] bg-violet-500/[0.05] rounded-full blur-[90px] pointer-events-none" />

        <div className="relative z-10 container mx-auto w-full max-w-full box-border px-4 sm:px-6 lg:px-8">
          {/* HERO */}
          <section className="min-w-0 lg:grid lg:grid-cols-[1.05fr_0.95fr] lg:gap-12 xl:gap-16 items-center mb-16 sm:mb-24 lg:mb-28">
            <div className="text-center lg:text-left pt-2 min-w-0">
              <div className="inline-flex max-w-full min-w-0 items-center justify-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-4 py-1.5 text-xs font-semibold tracking-wide text-amber-200/90 mb-5">
                <Sparkles className="w-3.5 h-3.5 text-amber-400 shrink-0" aria-hidden />
                <span className="break-words text-center">{p.badge}</span>
              </div>
              <h1 className="text-[1.65rem] sm:text-4xl lg:text-[2.65rem] xl:text-5xl font-black text-white leading-[1.12] tracking-tight mb-5 break-words">
                {p.heroTitle}
              </h1>
              <p className="text-base sm:text-lg text-white/55 font-light leading-relaxed max-w-xl mx-auto lg:mx-0 mb-6 break-words">
                {p.heroSubtitle}
              </p>
              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-2 mb-8">
                {p.heroBadges.map((badge) => (
                  <span
                    key={badge}
                    className="inline-flex items-center rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-[11px] font-medium text-white/65 backdrop-blur-sm"
                  >
                    {badge}
                  </span>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center justify-center lg:justify-start gap-3">
                <Button
                  type="button"
                  size="lg"
                  className="rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 text-slate-950 font-semibold shadow-lg shadow-amber-900/25 h-12 px-6"
                  onClick={openOrderWhatsapp}
                >
                  {p.btnQuote}
                  <ArrowRight className="w-4 h-4 ml-2" aria-hidden />
                </Button>
                <Button asChild size="lg" variant="outline" className="rounded-xl border-white/20 bg-white/[0.04] text-white hover:bg-white/10 hover:border-amber-500/30 h-12 px-6">
                  <a href={modelsMenuUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center">
                    {p.btnModels}
                    <ChevronRight className="w-4 h-4 ml-1" aria-hidden />
                  </a>
                </Button>
              </div>
            </div>
            <div className="mt-10 lg:mt-0 relative min-w-0">
              <DigitalInvitesHeroMockups mock={p.heroMock} />
            </div>
          </section>

          <DigitalInvitesCatalogSection copy={p} language={language} />

          <DigitalInvitesBenefitsSection copy={p} />

          <DigitalInvitesHowToOrderSection copy={p} />

          {/* FAQ */}
          <section id="digital-invites-faq" className="max-w-3xl mx-auto mb-20 sm:mb-28 scroll-mt-28 min-w-0">
            <div className="text-center mb-10">
              <h2 className="text-2xl sm:text-4xl font-black text-white break-words">{p.sectionFaqTitle}</h2>
              <p className="text-white/50 mt-3 font-light break-words">{p.sectionFaqLead}</p>
            </div>
            <Accordion type="single" collapsible className="rounded-2xl border border-white/[0.08] bg-slate-950/40 backdrop-blur-md px-4 sm:px-6 divide-y divide-white/[0.06]">
              {p.faqItems.map((item, i) => (
                <AccordionItem key={item.q} value={`faq-${i}`} className="border-white/[0.06]">
                  <AccordionTrigger className="text-white hover:text-amber-300 hover:no-underline text-left font-semibold py-5">{item.q}</AccordionTrigger>
                  <AccordionContent className="text-white/50 font-light leading-relaxed pb-5">{item.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          {/* CTA FINAL */}
          <section id="digital-invites-cta" className="max-w-5xl mx-auto mb-16 sm:mb-20 scroll-mt-28 relative rounded-[2rem] border border-white/[0.08] overflow-hidden shadow-[0_40px_120px_-48px_rgba(245,158,11,0.2)]">
            <div className="absolute inset-0 bg-gradient-to-br from-amber-500/[0.12] via-slate-950/95 to-violet-950/80 pointer-events-none" aria-hidden />
            <div className="relative px-6 py-10 sm:px-12 sm:py-14 lg:px-16 lg:py-16 text-center">
              <h2 className="text-2xl sm:text-4xl lg:text-[2.35rem] font-black text-white tracking-tight mb-4 max-w-3xl mx-auto leading-tight break-words px-1">{p.finalCtaTitle}</h2>
              <p className="text-white/55 font-light mb-10 max-w-2xl mx-auto leading-relaxed text-base sm:text-lg break-words px-1">{p.finalCtaSubtitle}</p>
              <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4">
                <Button
                  type="button"
                  size="lg"
                  className="rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 text-slate-950 font-bold px-8 h-12 shadow-lg shadow-amber-900/25"
                  onClick={openOrderWhatsapp}
                >
                  {p.btnQuote}
                  <ArrowRight className="w-4 h-4 ml-2 shrink-0" aria-hidden />
                </Button>
                <Button type="button" size="lg" variant="outline" className="rounded-xl border-emerald-500/40 bg-emerald-500/10 text-emerald-50 hover:bg-emerald-500/18 h-12 px-8 font-semibold" onClick={openWhatsapp}>
                  <MessageCircle className="w-4 h-4 mr-2 shrink-0" aria-hidden />
                  {p.btnWhatsapp}
                </Button>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default DigitalInvites;
